import { useEffect, useState } from "react";
import * as React from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";

import { useDebounce } from "hooks";

import { ICD10Code, useGetICD10CodesQuery } from "services/icd10-codes";
import getAlphabetLetterByIndex from "utils/getAlphabetLetterByIndex";

const AUDIOLOGY_ICD10_CODES_PREFIX = "H9";

type ICDCodesFormFieldProps = {
  required?: boolean;
  label?: React.ReactNode;
  showOnlyActive?: boolean;
  disabled?: boolean;
  disabledText?: string;
  onOptionChanged?: (value: ICD10Code[]) => void;
};

export type RhfICDCodesFormFieldProps<T extends FieldValues> =
  ICDCodesFormFieldProps & UseControllerProps<T>;

export default function ICDCodesFormField<T extends FieldValues>({
  control,
  name,
  label = "ICD-10 Codes",
  required = false,
  rules,
  showOnlyActive = true,
  disabled = false,
  disabledText,
  onOptionChanged
}: RhfICDCodesFormFieldProps<T>) {
  const [search, setSearch] = useState<string>("");
  const [options, setOptions] = useState<ICD10Code[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const { value: dSearch, isDebouncing } = useDebounce(
    search || AUDIOLOGY_ICD10_CODES_PREFIX
  );

  const { data: icdCodes, isLoading: isSearching } = useGetICD10CodesQuery({
    search: dSearch,
    is_active: showOnlyActive ? true : undefined
  });

  const {
    fieldState: { error },
    field: { onChange, onBlur, ...restField }
  } = useController<T>({
    name,
    control,
    rules: {
      required: {
        value: required,
        message: `${label || "Value"} is required`
      },
      ...rules
    }
  });

  const errorMessage = error?.message;

  const selectedICD10Codes = useWatch({ control, name });
  const disabledHelperText = disabled ? disabledText : "";
  const renderInput = React.useCallback(
    (params: any) => (
      <TextField
        {...params}
        error={Boolean(errorMessage || disabled)}
        helperText={errorMessage || disabledHelperText}
        required={required}
        disabled={disabled}
        InputLabelProps={{ disabled: false }}
        label={label}
        autoFocus={open && !disabled}
      />
    ),
    [label, open, required, errorMessage, disabled, disabledHelperText]
  );

  useEffect(() => {
    const icd10Codes = icdCodes || [];
    return setOptions([...(selectedICD10Codes ?? []), ...icd10Codes]);
  }, [search, icdCodes, selectedICD10Codes]);

  return (
    <Autocomplete
      disablePortal
      loading={isSearching || isDebouncing}
      fullWidth
      filterSelectedOptions
      disabled={disabled}
      onOpen={() => !disabled && setOpen(true)}
      onClose={() => setOpen(false)}
      open={open && !disabled}
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : `${option.code}: ${option?.description}`
      }
      renderOption={(props: object, option: ICD10Code) => (
        <ListItem {...props}>
          <ListItemText secondary={option.code}>
            {option.description}
          </ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option: ICD10Code, value: ICD10Code) =>
        option.id === value.id
      }
      multiple
      inputValue={search}
      onInputChange={(event, newInputValue, reason) => {
        if (Boolean(event) && !disabled) {
          if (reason === "input") {
            setSearch(newInputValue);
          } else {
            setSearch("");
          }
        }
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            data-testid={`selected_icd10_code_${getAlphabetLetterByIndex(
              index
            )}_${option.code}`}
            label={`${getAlphabetLetterByIndex(index)}. ${option.code}: ${
              option?.description
            }`}
            {...getTagProps({ index })}
          />
        ))
      }
      filterOptions={(x) => x}
      popupIcon={<ArrowDropDownIcon />}
      renderInput={renderInput}
      onChange={(_, data) => {
        if (!disabled) {
          onOptionChanged?.(data);
          onChange(data);
          onBlur();
        }
      }}
      {...restField}
    />
  );
}
